import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { AxiosError } from 'axios';
import { useChatLogs } from '../../features/chat-logs/hooks/useChatLogs';
import { useDomain } from '../../features/domains/hooks/useDomains';
import { formatDate } from '../../lib/utils';
import { chatLogService } from '../../services/chatLogService';
import { api } from '../../services/api';
import { ROUTES } from '../../config/routes';
import type { ChatSession, ApiResponse } from '../../types';
import { ProLockOverlay } from '../../components/common';
import { useAuthStore, selectUserPlan } from '../../stores';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatHistoryPageProps {
  embeddedDomainId?: string;
}

export const ChatHistoryPage: React.FC<ChatHistoryPageProps> = ({ embeddedDomainId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const domainId = embeddedDomainId ?? paramId;
  const isEmbedded = !!embeddedDomainId;
  const navigate = useNavigate();
  const { data: domainData } = useDomain(domainId!);
  const domain = domainData?.data;

  const { data, isLoading } = useChatLogs(domainId!, { limit: 100 });
  const sessions: ChatSession[] = data?.data?.sessions || [];

  const [isExporting, setIsExporting] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'lead'>('all');
  const [historyPage, setHistoryPage] = useState(1);
  const plan = useAuthStore(selectUserPlan);
  const HISTORY_PAGE_SIZE = 5;

  const [searchParams] = useSearchParams();
  const highlightSessionId = searchParams.get('session');
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(highlightSessionId);

  useEffect(() => {
    if (highlightSessionId && sessions.length > 0) {
      const idx = sessions.findIndex(s => s.session_id === highlightSessionId);
      if (idx !== -1) {
        const targetPage = Math.floor(idx / HISTORY_PAGE_SIZE) + 1;
        setHistoryPage(targetPage);
      }
    }
  }, [highlightSessionId, sessions]);

  useEffect(() => {
    if (highlightSessionId && sessions.length > 0) {
      const timer = setTimeout(() => {
        const el = document.querySelector('[data-session-id="' + CSS.escape(highlightSessionId) + '"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightSessionId, sessions, historyPage]);

  const handleExport = async () => {
    setIsExporting(true);
    const fmtLocal = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const to = fmtLocal(new Date());
    const from = fmtLocal(new Date(Date.now() - 90 * 86400000));
    try {
      await chatLogService.downloadChatLogs(domainId!, from, to, domain?.domain || 'domain', 'csv');
    } catch (err: unknown) {
      const d = (err as AxiosError<unknown>)?.response?.data;
      let code: string | undefined;
      if (d instanceof Blob) {
        try {
          code = JSON.parse(await d.text())?.error?.code;
        } catch { /* ignore */ }
      } else {
        code = (d as ApiResponse<unknown>)?.error?.code;
      }
      if (code === 'FEATURE_PRO_ONLY') {
        setShowProModal(true);
        api.post('/subscription/conversion-event', { event_type: 'banner_shown', source: 'csv_lock' }).catch(() => {});
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleModalCTA = () => {
    api.post('/subscription/conversion-event', { event_type: 'cta_click', source: 'csv_lock' }).catch(() => {});
    navigate(ROUTES.SUBSCRIPTION);
  };

  return (
    <div data-testid="chat-history-page" style={{ maxWidth: '800px' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* page-head: タイトルは独立ルート時のみ表示 */}
      {!isEmbedded && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>会話履歴</div>
          <div style={{ fontSize: '13px', color: '#94A3B8' }}>
            {domain ? `${domain.description || domain.domain} のお客様との会話を確認できます。` : 'お客様とのやりとりを確認できます。'}
          </div>
        </div>
      )}
      {/* CSV/JSONボタンはisEmbedded時も常時表示 */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', justifyContent: 'flex-end' }}>
        <button
          data-testid="chat-history-export"
          onClick={() => handleExport()}
          disabled={isExporting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: isExporting ? 'default' : 'pointer', flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>
          </svg>
          CSVで書き出す
          <span style={{ background: '#FEF3C7', color: '#B45309', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', marginLeft: '4px' }}>PRO</span>
        </button>
      </div>

      {/* フィルタータブ */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        {([
          { key: 'all', label: 'すべて' },
          { key: 'lead', label: 'リード獲得' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setActiveFilter(key); setHistoryPage(1); }}
            style={{
              padding: '5px 14px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: key === activeFilter ? 700 : 400,
              border: '1.5px solid',
              borderColor: key === activeFilter ? '#0a7a72' : '#E2E8F0',
              background: key === activeFilter ? '#0a7a72' : '#fff',
              color: key === activeFilter ? '#fff' : '#475569',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* テーブルカード */}
      {(() => {
        const filteredSessions = activeFilter === 'lead' ? sessions.filter(s => (s as any).lead_captured) : sessions;
        const totalPages = Math.max(1, Math.ceil(filteredSessions.length / HISTORY_PAGE_SIZE));
        const safePage = Math.min(historyPage, totalPages);
        const startIdx = (safePage - 1) * HISTORY_PAGE_SIZE;
        const pagedSessions = filteredSessions.slice(startIdx, startIdx + HISTORY_PAGE_SIZE);
        return (
          <>
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', overflowX: 'auto' }}>
              {isLoading ? (
                <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>読み込み中…</div>
              ) : filteredSessions.length === 0 ? (
                <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                  該当する会話履歴がありません
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                      <th style={{ textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#94A3B8', padding: '12px 16px', whiteSpace: 'nowrap' }}>日時</th>
                      <th style={{ textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#94A3B8', padding: '12px 16px' }}>最初の質問</th>
                      <th style={{ textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#94A3B8', padding: '12px 16px', whiteSpace: 'nowrap' }}>メッセージ数</th>
                      <th style={{ padding: '12px 16px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSessions.map((session, i) => {
                      const firstUserMsg = session.messages.find(m => m.role === 'user')?.content;
                      const msgCount = session.messages.filter((m: any) => m.role === 'user').length;
                      const isExpanded = expandedSessionId === session.session_id;
                      return (
                        <React.Fragment key={session.session_id}>
                          <tr data-testid="chat-session-item" data-session-id={session.session_id} style={{ borderTop: i > 0 ? '1px solid #F1F5F9' : 'none' }}>
                            <td style={{ fontSize: '13px', color: '#94A3B8', padding: '14px 16px', whiteSpace: 'nowrap' }}>
                              {formatDate(session.started_at, 'M/d HH:mm')}
                            </td>
                            <td style={{ fontSize: '14px', color: '#0F172A', padding: '14px 16px', maxWidth: '280px' }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {firstUserMsg || '(内容なし)'}
                              </div>
                            </td>
                            <td style={{ fontSize: '14px', color: '#64748B', padding: '14px 16px', whiteSpace: 'nowrap' }}>
                              {msgCount}
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                              <button
                                data-testid={`chat-session-detail-${session.session_id}`}
                                onClick={() => setExpandedSessionId(prev => prev === session.session_id ? null : session.session_id)}
                                style={{ fontSize: '13px', color: '#0a7a72', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                              >
                                詳細
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={4}>
                                <div style={{ padding: '16px', background: '#F8FAFC' }}>
                                  {session.messages.map((msg, idx) => (
                                    <div key={idx} style={{ marginBottom: '8px' }}>
                                      <span style={{ fontWeight: 700, color: msg.role === 'user' ? '#0a7a72' : '#64748B' }}>
                                        {msg.role === 'user' ? 'ユーザー' : 'ボット'}:
                                      </span>
                                      {msg.role === 'user' ? (
                                        <span style={{ marginLeft: '8px', whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                                      ) : (
                                        <div style={{ marginLeft: '8px', display: 'inline-block', fontSize: '14px' }}>
                                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                            p: ({children}) => <div style={{ marginBottom: '4px' }}>{children}</div>,
                                            ul: ({children}) => <ul style={{ listStyleType: 'disc', paddingLeft: '16px', marginBottom: '4px' }}>{children}</ul>,
                                            ol: ({children}) => <ol style={{ listStyleType: 'decimal', paddingLeft: '16px', marginBottom: '4px' }}>{children}</ol>,
                                            li: ({children}) => <li style={{ marginBottom: '2px' }}>{children}</li>,
                                            strong: ({children}) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                            h1: ({children}) => <div style={{ fontWeight: 700, fontSize: '14px', marginTop: '4px', marginBottom: '2px' }}>{children}</div>,
                                            h2: ({children}) => <div style={{ fontWeight: 700, fontSize: '14px', marginTop: '4px', marginBottom: '2px' }}>{children}</div>,
                                            h3: ({children}) => <div style={{ fontWeight: 600, fontSize: '14px', marginTop: '4px', marginBottom: '2px' }}>{children}</div>,
                                            code: ({children}) => <code style={{ background: '#F1F5F9', borderRadius: '3px', padding: '0 2px', fontSize: '12px', fontFamily: 'monospace' }}>{children}</code>,
                                            pre: ({children}) => <pre style={{ background: '#F1F5F9', borderRadius: '6px', padding: '8px', fontSize: '12px', overflow: 'auto', margin: '4px 0' }}>{children}</pre>,
                                            table: ({children}) => <table style={{ borderCollapse: 'collapse', fontSize: '12px', margin: '4px 0', width: '100%' }}>{children}</table>,
                                            th: ({children}) => <th style={{ border: '1px solid #E2E8F0', padding: '2px 6px', background: '#F1F5F9', fontWeight: 600 }}>{children}</th>,
                                            td: ({children}) => <td style={{ border: '1px solid #E2E8F0', padding: '2px 6px' }}>{children}</td>,
                                          }}>{msg.content}</ReactMarkdown>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {/* ページネーション */}
            {filteredSessions.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', fontSize: '13px', color: '#64748B' }}>
                <span>全{filteredSessions.length}件中 {startIdx + 1}-{Math.min(startIdx + HISTORY_PAGE_SIZE, filteredSessions.length)}件を表示</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: '13px', cursor: safePage === 1 ? 'default' : 'pointer', opacity: safePage === 1 ? 0.4 : 1 }}
                  >
                    前へ
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = i + 1;
                    return (
                      <button
                        key={p}
                        onClick={() => setHistoryPage(p)}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid', borderColor: p === safePage ? '#0a7a72' : '#E2E8F0', background: p === safePage ? '#0a7a72' : '#fff', color: p === safePage ? '#fff' : '#475569', fontSize: '13px', fontWeight: p === safePage ? 700 : 400, cursor: 'pointer' }}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: '13px', cursor: safePage === totalPages ? 'default' : 'pointer', opacity: safePage === totalPages ? 0.4 : 1 }}
                  >
                    次へ
                  </button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* 下部ロック注記 */}
      {plan !== 'pro' && plan !== 'business' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '8px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span style={{ fontSize: '12px', color: '#94A3B8' }}>
            CSVでの書き出しはProプランからご利用いただけます。閲覧は今のプランのままできます。
          </span>
        </div>
      )}

      {/* Pro upsell モーダル */}
      {showProModal && (
        <ProLockOverlay
          variant="modal"
          title={<>CSV書き出しは<span style={{ color: '#0a7a72' }}>Proプラン</span>から</>}
          description="会話履歴のCSV書き出しはProプランの機能です。アップグレードすると、会話データやリードを書き出して分析・営業活動にご活用いただけます。"
          onUpgrade={handleModalCTA}
          onDismiss={() => setShowProModal(false)}
        />
      )}

    </div>
  );
};
